import { Injectable } from '@angular/core';
import { DynamicPrizeConfig, PrizeType, PrizeTemplate } from '@interface/ruleta.interface';

/**
 * Servicio de configuración de ruleta
 * Centraliza toda la configuración de premios, rangos y templates
 */
@Injectable({
  providedIn: 'root',
})
export class RouletteConfigService {
  public readonly dynamicConfig: DynamicPrizeConfig = {
    amountRanges: [
      {
        min: 50000,
        max: 999999,
        eligiblePrizeTypes: [PrizeType.MEJOR_TASA],
      },
      {
        min: 1000000,
        max: 5000000,
        eligiblePrizeTypes: [
          PrizeType.BONIFICACION_060,
          PrizeType.BONIFICACION_040,
          PrizeType.BONIFICACION_020,
          PrizeType.MEJOR_TASA,
        ],
      },
      {
        min: 5000001,
        max: 9999999,
        eligiblePrizeTypes: [
          PrizeType.BONIFICACION_040,
          PrizeType.BONIFICACION_020,
          PrizeType.MEJOR_TASA,
          PrizeType.BONO_20K,
        ],
      },
      {
        min: 10000000,
        max: 19999999,
        eligiblePrizeTypes: [
          PrizeType.BONIFICACION_040,
          PrizeType.BONIFICACION_020,
          PrizeType.MEJOR_TASA,
          PrizeType.BONO_20K,
          PrizeType.BONO_50K,
        ],
      },
      {
        min: 20000000,
        max: 50000000,
        eligiblePrizeTypes: [
          PrizeType.BONIFICACION_020,
          PrizeType.MEJOR_TASA,
          PrizeType.BONO_20K,
          PrizeType.BONO_50K,
          PrizeType.BONO_100K,
        ],
      },
      {
        min: 50000001,
        max: 149999999,
        eligiblePrizeTypes: [
          PrizeType.MEJOR_TASA,
          PrizeType.BONO_20K,
          PrizeType.BONO_50K,
          PrizeType.BONO_100K,
        ],
      },
      {
        min: 150000000,
        max: Number.MAX_SAFE_INTEGER,
        eligiblePrizeTypes: [
          PrizeType.MEJOR_TASA,
          PrizeType.BONO_20K,
          PrizeType.BONO_50K,
          PrizeType.BONO_100K,
          PrizeType.BONO_1M,
        ],
      },
    ],
    baseProbabilities: {
      [PrizeType.MEJOR_TASA]: 0.2,
      [PrizeType.BONO_1M]: 0.1, // +1 MILLÓN (más raro)
      [PrizeType.BONO_100K]: 0.15, // +100 MIL
      [PrizeType.BONO_50K]: 0.2, // +50 MIL
      [PrizeType.BONO_20K]: 0.25, // +20 MIL (más común)
      [PrizeType.BONIFICACION_020]: 0.25,
      [PrizeType.BONIFICACION_040]: 0.2,
      [PrizeType.BONIFICACION_060]: 0.15,
      [PrizeType.SIGUE_PARTICIPANDO]: 0.4,
    },
    minOptions: 6,
    maxOptions: 8,
  };
  public readonly prizeTemplates: Record<PrizeType, PrizeTemplate> = {
    [PrizeType.MEJOR_TASA]: {
      type: PrizeType.MEJOR_TASA,
      label: 'La mejor tasa',
      color: 'linear-gradient(-90deg, rgba(35, 149, 190, 1) 0%, rgba(27, 141, 198, 1) 19.07%, rgba(25, 147, 200, 1) 44.86%, rgba(13, 122, 212, 1) 74.34%, rgba(0, 169, 225, 1) 100%);',
      textColor: '#FFFFFF',
      bonus_type: 'MEJOR_TASA_MERCADO',
      value: 'mejor_tasa',
      bonusTag: 'MEJOR_TASA',
      tag: 'del mercado',
    },
    [PrizeType.BONO_1M]: {
      type: PrizeType.BONO_1M,
      label: '+1 MILLÓN',
      color: 'linear-gradient(214.15deg, rgba(189, 104, 22, 1) 0%, rgba(201, 153, 19, 1) 38.1%, rgba(204, 158, 21, 1) 48.95%, rgba(212, 171, 26, 1) 61.17%, rgba(225, 194, 35, 1) 74.06%, rgba(244, 225, 47, 1) 87.32%, rgba(249, 233, 50, 1) 90.26%);',
      textColor: '#FFFFFF',
      bonus_type: 'ESCALONADO_1M',
      value: '1000000',
      bonusTag: '1M',
      tag: 'Bono',
    },
    [PrizeType.BONO_100K]: {
      type: PrizeType.BONO_100K,
      label: '+100 MIL',
      color: 'linear-gradient(101.96deg, rgba(39, 115, 184, 1) 70.18%, rgba(44, 76, 154, 1) 85.76%);',
      textColor: '#FFFFFF',
      bonus_type: 'ESCALONADO_100K',
      value: '100000',
      bonusTag: '100k',
      tag: 'Bono',
    },
    [PrizeType.BONO_50K]: {
      type: PrizeType.BONO_50K,
      label: '+50 MIL',
      color: 'linear-gradient(-90deg, rgba(35, 190, 107, 1) 0%, rgba(32, 193, 113, 1) 19.07%, rgba(25, 200, 130, 1) 44.86%, rgba(13, 212, 159, 1) 74.34%, rgba(0, 225, 189, 1) 100%);',
      textColor: '#FFFFFF',
      bonus_type: 'ESCALONADO_50K',
      value: '50000',
      bonusTag: '50k',
      tag: 'Bono',
    },
    [PrizeType.BONO_20K]: {
      type: PrizeType.BONO_20K,
      label: '+20 MIL',
      color: 'linear-gradient(146.99deg, rgba(57, 168, 53, 1) 0.08%, rgba(0, 140, 54, 1) 100%);',
      textColor: '#FFFFFF',
      bonus_type: 'ESCALONADO_20K',
      value: '20000',
      bonusTag: '20k',
      tag: 'Bono',
    },
    [PrizeType.BONIFICACION_020]: {
      type: PrizeType.BONIFICACION_020,
      label: '+0.20%',
      color: 'linear-gradient(146.99deg, rgba(34, 127, 31, 1) 0.08%, rgba(3, 110, 44, 1) 100%);',
      textColor: 'white',
      bonus_type: 'PORCENTUAL_020',
      value: '+0.20%',
      bonusTag: 'porcentual',
      tag: 'Cashback',
    },
    [PrizeType.BONIFICACION_040]: {
      type: PrizeType.BONIFICACION_040,
      label: '+0.40%',
      color: '#FF9F43',
      textColor: 'white',
      bonus_type: 'PORCENTUAL_040',
      value: '+0.40%',
      bonusTag: 'porcentual',
      tag: 'Cashback',
    },
    [PrizeType.BONIFICACION_060]: {
      type: PrizeType.BONIFICACION_060,
      label: '+0.60%',
      color: 'linear-gradient(101.96deg, rgba(39, 115, 184, 1) 70.18%, rgba(44, 76, 154, 1) 85.76%);',
      textColor: '#FFFFFF',
      bonus_type: 'PORCENTUAL_060',
      value: '+0.60%',
      bonusTag: 'porcentual',
      tag: 'Cashback',
    },
    [PrizeType.SIGUE_PARTICIPANDO]: {
      type: PrizeType.SIGUE_PARTICIPANDO,
      label: 'Sigue participando',
      color: '#DDA0DD',
      textColor: 'white',
      bonus_type: 'NO_BONUS',
      value: '0',
      bonusTag: 'SIGUE_PARTICIPANDO',
    },
  };
}
